/* eslint-disable max-lines */
// TODO: задача на рефакторинг https://jira.cian.tech/browse/CD-189369

import { v4 as uuid } from 'uuid';

import { IOfferSchema } from '../repositories/chats/entities/get_my_offers_for_selection/OfferSchema';
import { ERequestApiStatus } from '../types/appState';
import { TMessageFiles } from '../types/messageFiles';
import { EMessageType, EOutcomingStatus, TMessage, TMessageSchema, TOutcomingStatus } from '../types/messages';
import { TOpenedChats } from '../types/openedChats';

// Массив даты [Год, Месяц, Число]
type TTimePage = [number, number, number];

export interface IParsedMessage {
  message: TMessage;
  messageId: string;
  chatId: string;
  // Идентификатор в виде Год/Месяц/Число
  timePageKey: string;
  timePage: TTimePage;
  uid: string;
}

/**
 * Элемент Map'а сообщений, который в себе содержит "адрес" сообщения в странице
 * и распаршенное сообщение.
 */
export interface IMessageItem {
  // индекс страницы к которому прендалежит сообщение
  pageIndex: number;
  // индекс сообщения в массиве сообщений страницы
  messageIndex: number;
  // распаршенное сообщение
  message: IParsedMessage;
}

export type TMessagesMap = Map<string, IMessageItem>;

// При загрузке сообщения на бек мы формируем messageId через uuid,
// обратно по веб-сокету это значение возвращается в поле uid, а messageId формирует бек
function parseMessage(
  {
    createdMs,
    messageId,
    uid,
    userId: messageUserId,
    byMasterAgent,
    actionButtons,
    text,
    htmlText,
    read,
    unread,
    chatId,
    files,
    offers,
    assumptions,
    bubbles,
    type,
  }: TMessageSchema,
  userId: number,
  status?: TOutcomingStatus,
): IParsedMessage {
  const date = createdMs ? new Date(createdMs) : null;

  const timePage: TTimePage = date
    ? [date.getFullYear(), date.getMonth(), date.getDate()]
    : ([] as unknown as TTimePage);

  const isOutcoming = Boolean(messageUserId === userId || byMasterAgent);

  return {
    messageId,
    uid: uid || '',
    chatId,
    timePage,
    timePageKey: `${timePage[0]}/${timePage[1]}/${timePage[2]}`,
    message: {
      actionButtons,
      byMasterAgent,
      created: createdMs as number,
      message: text || '',
      htmlText,
      userId: messageUserId,
      files,
      offers,
      type,
      isOutcoming,
      ...(isOutcoming
        ? {
            status: read ? EOutcomingStatus.Read : status || EOutcomingStatus.Fulfilled,
          }
        : {
            isRead: !unread,
          }),
      assumptions,
      bubbles,
    },
  };
}

export interface IMessagePage {
  date: number;
  messages: IParsedMessage[];
  // Идентификатор в виде Год/Месяц/Число
  timeCode: string;
}

interface IOutcomingMessageParams {
  chatId: string;
  userId: number;
  type?: EMessageType;
  text?: string;
  files?: TMessageFiles;
  offers?: IOfferSchema[];
  uid?: string;
}

export function createOutcomingMessage({
  text,
  chatId,
  userId,
  type = EMessageType.Text,
  uid,
}: IOutcomingMessageParams): TMessageSchema {
  const messageId = uid || uuid();

  return {
    created: 0,
    createdMs: new Date().getTime(),
    text,
    userId,
    messageId,
    chatId,
    read: false,
    unread: false,
    type,
    byMasterAgent: undefined,
    actionButtons: [],
    assumptions: undefined,
    offers: [],
    bubbles: [],
  };
}

function createMessagePage([year, month, day]: [number, number, number], timeCode: string): IMessagePage {
  return {
    date: new Date(year, month, day).getTime(),
    messages: [],
    timeCode,
  };
}

export function setMessageReadStatus(messagesMap: TMessagesMap, messageIds: string[], pages: IMessagePage[]) {
  const nextPages = pages.slice();

  messageIds.forEach(id => {
    const messageItem = messagesMap.get(id);

    if (messageItem) {
      const { message, messageIndex, pageIndex } = messageItem;

      const nextParseMessage: IParsedMessage = {
        ...message,
        message: {
          ...message.message,
          ...(message.message.isOutcoming ? { status: 'read' } : { isRead: true }),
        },
      };
      messagesMap.set(id, {
        messageIndex,
        pageIndex,
        message: nextParseMessage,
      });

      if (nextPages[pageIndex] && Array.isArray(nextPages[pageIndex].messages)) {
        nextPages[pageIndex].messages[messageIndex] = nextParseMessage;
      } else {
        console.error(`Couldn't set 'read' status for the message. Message not found.`);
      }
    }
  });

  return nextPages;
}

export function removeMessageFromPage(messageId: string, messagesMap: TMessagesMap, pages: IMessagePage[]) {
  const nextPages = pages.slice();

  const messageItemToDelete = messagesMap.get(messageId);

  // если есть сообщение по запрошенному id, удалить его - оно вставиться заново с новой датой.
  if (messageItemToDelete) {
    const { pageIndex } = messageItemToDelete;

    // удаляем старое сообщение
    nextPages[pageIndex].messages = nextPages[pageIndex].messages.filter(message => message.messageId !== messageId);

    // Если на странице нет сообщений, удаляем страницу
    if (!nextPages[pageIndex].messages.length) {
      nextPages.splice(pageIndex, 1);
    }

    messagesMap.delete(messageId);
  }

  return nextPages;
}

function handleRawMessage(
  rawMessage: TMessageSchema,
  messagesMap: TMessagesMap,
  nextPages: IMessagePage[],
  userId: number,
  status?: TOutcomingStatus,
): boolean {
  const messageItem = messagesMap.get(rawMessage.messageId);

  // если сообщение есть, значит пришло обновление о прочитанном статусе либо получателем, либо отправителем
  if (messageItem) {
    const { message, messageIndex, pageIndex } = messageItem;

    // если понадобится когда-то возможность редактирования сообщения, то это надо сделать тут.
    const parsedMessage: IParsedMessage = {
      ...message,
      message: {
        ...message.message,
        ...(message.message.isOutcoming ? { status: 'read' } : { isRead: !rawMessage.unread }),
        actionButtons: rawMessage.actionButtons,
        bubbles: rawMessage.bubbles,
        htmlText: rawMessage.htmlText,
        message: rawMessage.text || message.message.message,
      },
    };

    messagesMap.set(rawMessage.messageId, {
      message: parsedMessage,
      messageIndex,
      pageIndex,
    });

    if (nextPages[pageIndex]?.messages) {
      nextPages[pageIndex].messages[messageIndex] = parsedMessage;
    }

    return false;
  }

  const parsedMessage = parseMessage(rawMessage, userId, status);

  const pageIndex = nextPages.findIndex(({ timeCode }) => timeCode === parsedMessage.timePageKey);

  // если есть страница, обновляем ее
  if (pageIndex >= 0) {
    nextPages[pageIndex] = {
      ...nextPages[pageIndex],
      messages: nextPages[pageIndex].messages.concat(parsedMessage),
    };

    return true;
  }

  // создаем новую страницу и добавляем в нее сообщение
  const page = createMessagePage(parsedMessage.timePage, parsedMessage.timePageKey);

  page.messages.push(parsedMessage);

  nextPages.push(page);

  return true;
}

const clonePages = (pages: IMessagePage[]) =>
  pages?.slice()?.map(({ messages, ...rest }) => ({
    ...rest,
    messages: messages.slice(),
  }));

const updatePagesIndexes = (pages: IMessagePage[], messagesMap: TMessagesMap) =>
  pages
    .sort((a, b) => a.date - b.date)
    .forEach((page, pageIndex) => {
      page.messages
        .sort((a, b) => a.message.created - b.message.created)
        .forEach((message, messageIndex) =>
          messagesMap.set(message.messageId, {
            message,
            messageIndex,
            pageIndex,
          }),
        );
    });

export function insertMessageInPage(
  messagesMap: TMessagesMap,
  rawMessage: TMessageSchema,
  pages: IMessagePage[],
  userId: number,
  status?: TOutcomingStatus,
) {
  const nextPages = clonePages(pages);

  const indexesChanged = handleRawMessage(rawMessage, messagesMap, nextPages, userId, status);

  if (indexesChanged) {
    updatePagesIndexes(nextPages, messagesMap);
  }

  return nextPages;
}

function updateMessagesInPages(
  messagesMap: TMessagesMap,
  rawMessages: TMessageSchema[],
  pages: IMessagePage[],
  userId: number,
) {
  const nextPages = pages.slice();

  let indexesChanged = false;

  rawMessages.forEach(
    rawMessage => (indexesChanged = handleRawMessage(rawMessage, messagesMap, nextPages, userId) || indexesChanged),
  );

  if (indexesChanged) {
    updatePagesIndexes(nextPages, messagesMap);
  }

  return nextPages;
}

type TAddMessagesInChatParams = {
  openedChats: TOpenedChats;
  messages: TMessageSchema[];
  currentOpenedChatId: string;
  userId: number;
  messagesMap: TMessagesMap;
};

export const addMessagesInChat = ({
  openedChats,
  messages,
  currentOpenedChatId,
  userId,
  messagesMap,
}: TAddMessagesInChatParams): TOpenedChats => {
  const currentOpenedChat = openedChats[currentOpenedChatId];

  if (!currentOpenedChat) {
    return openedChats;
  }

  if (!messages.length) {
    return {
      ...openedChats,
      [currentOpenedChatId]: {
        ...currentOpenedChat,
        allMessagesLoaded: true,
        status: ERequestApiStatus.Succeed,
      },
    };
  }

  const nextPages = updateMessagesInPages(messagesMap, messages, currentOpenedChat.pages, userId);

  return {
    ...openedChats,
    [currentOpenedChatId]: {
      ...currentOpenedChat,
      currentTimestamp: nextPages[0]?.messages[0]?.message.created,
      pages: nextPages,
      status: ERequestApiStatus.Succeed,
    },
  };
};
